/**
 * 📊 OrganizationalReportsService.js - PASO 5.12.2
 * Servicio para reportes de estructura organizacional
 * 
 * @author Sistema MGI
 * @version 1.1 - Corregido para usar api-client del sistema
 * @date 27 de Agosto, 2025
 */

import apiClient from '@/services/api-client'

class OrganizationalReportsService {
  constructor() {
    this.baseUrl = '/reports/organizational'
  }

  /**
   * 📋 Obtener reporte de estructura organizacional
   * @returns {Promise<Object>} Reporte de estructura grupos-agencias
   */
  async getStructureReport() {
    try {
      console.log('📋 Obteniendo reporte de estructura organizacional...')
      const response = await apiClient.get(`${this.baseUrl}/structure`)
      return response.data
    } catch (error) {
      console.error('❌ Error obteniendo reporte de estructura:', error)
      throw error
    }
  }

  /**
   * 📊 Obtener reporte de capacidades por grupo
   * @returns {Promise<Object>} Reporte de capacidades
   */
  async getCapacityReport() {
    try {
      console.log('📊 Obteniendo reporte de capacidades...')
      const response = await apiClient.get(`${this.baseUrl}/capacity`)
      return response.data
    } catch (error) {
      console.error('❌ Error obteniendo reporte de capacidades:', error)
      throw error
    }
  }

  /**
   * 🗺️ Obtener reporte de distribución geográfica
   * @returns {Promise<Object>} Reporte de distribución geográfica
   */
  async getGeographicReport() {
    try {
      console.log('🗺️ Obteniendo reporte de distribución geográfica...')
      const response = await apiClient.get(`${this.baseUrl}/geographic`)
      return response.data
    } catch (error) {
      console.error('❌ Error obteniendo reporte geográfico:', error)
      throw error
    }
  }

  /**
   * � Exportar reporte a Excel
   * @param {string} reportType - Tipo de reporte (structure, capacity, geographic)
   * @returns {Promise<Blob>} Archivo Excel como blob
   */
  async exportToExcel(reportType) {
    try {
      console.log(`� Exportando reporte ${reportType} a Excel...`)
      const response = await apiClient.request({
        url: `${this.baseUrl}/export/excel/${reportType}`,
        method: 'GET',
        responseType: 'blob',
        timeout: 60000 // 1 minuto para exports
      })
      
      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      
      return blob
    } catch (error) {
      console.error(`❌ Error exportando reporte ${reportType} a Excel:`, error)
      throw error
    }
  }

  /**
   * 📄 Exportar reporte a PDF
   * @param {string} reportType - Tipo de reporte (structure, capacity, geographic)
   * @returns {Promise<Blob>} Archivo PDF como blob
   */
  async exportToPDF(reportType) {
    try {
      console.log(`📄 Exportando reporte ${reportType} a PDF...`)
      const response = await apiClient.request({
        url: `${this.baseUrl}/export/pdf/${reportType}`,
        method: 'GET',
        responseType: 'blob',
        timeout: 60000 // 1 minuto para exports
      })
      
      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: 'application/pdf'
      })
      
      return blob
    } catch (error) {
      console.error(`❌ Error exportando reporte ${reportType} a PDF:`, error)
      throw error
    }
  }

  /**
   * 📥 Descargar archivo de reporte
   * @param {Blob} blob - Archivo como blob
   * @param {string} filename - Nombre del archivo
   */
  downloadFile(blob, filename) {
    try {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      
      // Limpiar
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      console.log(`✅ Archivo ${filename} descargado exitosamente`)
    } catch (error) {
      console.error('❌ Error descargando archivo:', error)
      throw error
    }
  }

  /**
   * 📑 Exportar y descargar reporte en Excel
   * @param {string} reportType - Tipo de reporte
   * @param {string} customFilename - Nombre personalizado (opcional)
   */
  async downloadExcelReport(reportType, customFilename = null) {
    try {
      const blob = await this.exportToExcel(reportType)
      const filename = customFilename || `MGI_Reporte_${reportType}_${new Date().toISOString().split('T')[0]}.xlsx`
      this.downloadFile(blob, filename)
    } catch (error) {
      console.error('❌ Error descargando reporte Excel:', error)
      throw error
    }
  }

  /**
   * 📄 Exportar y descargar reporte en PDF
   * @param {string} reportType - Tipo de reporte
   * @param {string} customFilename - Nombre personalizado (opcional)
   */
  async downloadPDFReport(reportType, customFilename = null) {
    try {
      const blob = await this.exportToPDF(reportType)
      const filename = customFilename || `MGI_Reporte_${reportType}_${new Date().toISOString().split('T')[0]}.pdf`
      this.downloadFile(blob, filename)
    } catch (error) {
      console.error('❌ Error descargando reporte PDF:', error)
      throw error
    }
  }

  /**
   * 📊 Obtener resumen de todos los reportes
   * @returns {Promise<Object>} Resumen consolidado de reportes
   */
  async getAllReportsSummary() {
    try {
      console.log('📊 Obteniendo resumen de todos los reportes...')
      
      const [structureReport, capacityReport, geographicReport] = await Promise.all([
        this.getStructureReport(),
        this.getCapacityReport(),
        this.getGeographicReport()
      ])

      return {
        structure: {
          summary: structureReport.summary,
          generated_at: structureReport.generated_at
        },
        capacity: {
          system_totals: capacityReport.system_totals,
          generated_at: capacityReport.generated_at
        },
        geographic: {
          total_regions: Object.keys(geographicReport.geographic_data || {}).length,
          generated_at: geographicReport.generated_at
        },
        consolidated_at: new Date().toISOString()
      }
    } catch (error) {
      console.error('❌ Error obteniendo resumen de reportes:', error)
      throw error
    }
  }

  /**
   * 🎨 Obtener metadatos de reportes disponibles
   * @returns {Array} Lista de reportes disponibles con metadatos
   */
  getAvailableReports() {
    return [
      {
        id: 'structure',
        name: 'Estructura Organizacional',
        description: 'Grupos con sus agencias, supervisores y estadísticas',
        icon: 'mdi-sitemap',
        color: 'primary',
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'capacity',
        name: 'Capacidades por Grupo',
        description: 'Análisis de capacidades y recursos por grupo',
        icon: 'mdi-chart-bar',
        color: 'success',
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'geographic',
        name: 'Distribución Geográfica',
        description: 'Distribución de grupos y agencias por región',
        icon: 'mdi-map-marker-multiple',
        color: 'warning',
        exportFormats: ['excel', 'pdf']
      }
    ]
  }

  /**
   * 🔄 Validar conexión con API de reportes
   * @returns {Promise<boolean>} Estado de la conexión
   */
  async validateConnection() {
    try {
      // Intentar obtener un reporte simple para validar
  await apiClient.get('/reports/organizational/structure', { timeout: 5000 })
      return true
    } catch (error) {
      console.warn('⚠️ No se pudo conectar con la API de reportes:', error.message)
      return false
    }
  }
}

export default new OrganizationalReportsService()
